import { useEffect, useState } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import type { IntercomResponse } from '@/features/Setting/Integrations/integrationRedirection/intercom';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getIntercomUrl } from '@/utils/integration';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { ImportRuleT } from '../types';
import IntercomImportSettingModal from './IntercomImportSettingModal';
import { intercomImportTemplate } from './intercomImportTemplate';
import IntercomTriggerSettingModal from './IntercomTriggerSettingModal';
import { useIntercom } from './useIntercom';

export const IntercomItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    intercomWorkspace,
    disconnectMutation,
    requestImportMutation,
    updateImportOptionMutation,
    updateTriggerOptionMutation,
    invalidateIntercomQuery,
  } = useIntercom(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<IntercomResponse | null>('intercom-redirect', null);

  const importSettingModalState = useModalState<{
    isInitialFlow: boolean;
  }>({ isInitialFlow: false });
  const triggerSettingModalState = useModalState<{
    isInitialFlow: boolean;
  }>({ isInitialFlow: false });
  const onetimeImportModalState = useModalState<{
    isInitialFlow: boolean;
  }>({ isInitialFlow: false });

  const [pendingInitialFlow, setPendingInitialFlow] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const importRule: ImportRuleT = intercomWorkspace?.import_rule || {
    status: 1,
    bound: 1,
    tag: 1,
    tag_names: [],
  };

  const closeAllModals = async () => {
    await Promise.all([
      importSettingModalState.close(),
      triggerSettingModalState.close(),
      onetimeImportModalState.close(),
    ]);
  };

  const openImportSettingModal = async (isInitialFlow = false) => {
    await closeAllModals();
    importSettingModalState.open({
      modalState: { isInitialFlow },
    });
  };

  const openTriggerSettingModal = async (isInitialFlow = false) => {
    await closeAllModals();
    triggerSettingModalState.open({
      modalState: { isInitialFlow },
    });
  };

  const openOnetimeImportModal = async (isInitialFlow = false) => {
    await closeAllModals();
    onetimeImportModalState.open({
      modalState: { isInitialFlow },
    });
  };

  const closeModal = async () => {
    await closeAllModals();
    setModalLoading(false);
  };

  const handleConnect = () => {
    if (!currWorkspace) return;
    window.open(getIntercomUrl(currWorkspace.alias));
  };

  const handleDisconnect = async () => {
    await disconnectMutation();
  };

  const handleImportWorkspace = async (
    start_date: string,
    end_date: string,
    importRuleValue: ImportRuleT,
  ) => {
    setModalLoading(true);
    try {
      await updateImportOptionMutation(importRuleValue);
      await requestImportMutation({ start_date, end_date });
    } catch {
      toast('Failed to update import option', 'fail');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveWithoutImport = async (importRuleValue: ImportRuleT) => {
    setModalLoading(true);
    try {
      await updateImportOptionMutation(importRuleValue);
    } catch {
      toast('Failed to update import option', 'fail');
    } finally {
      setModalLoading(false);
    }
  };

  const saveImportOption = async (
    importRuleValue: ImportRuleT,
    isInitialFlow: boolean,
  ) => {
    setModalLoading(true);
    try {
      await updateImportOptionMutation(importRuleValue);
      if (isInitialFlow) {
        await openTriggerSettingModal(true);
      } else {
        await closeModal();
      }
    } catch {
      toast('Failed to update import option', 'fail');
    } finally {
      setModalLoading(false);
    }
  };

  const saveTriggerOption = async (
    importRuleValue: ImportRuleT,
    isInitialFlow: boolean,
  ) => {
    setModalLoading(true);
    try {
      await updateTriggerOptionMutation(importRuleValue);
      if (isInitialFlow) {
        await openOnetimeImportModal(true);
      } else {
        await closeModal();
      }
    } catch (e) {
      toast('Failed to update trigger option', 'fail');
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    invalidateIntercomQuery();
    fetchFeedbackSources(currWorkspace.alias);
    setPendingInitialFlow(true);
  }, [
    currWorkspace,
    fetchFeedbackSources,
    invalidateIntercomQuery,
    isConnector,
    redirect,
    removeKey,
    setRedirect,
  ]);

  useEffect(() => {
    if (!pendingInitialFlow) return;
    if (!intercomWorkspace) return;

    setPendingInitialFlow(false);
    void openImportSettingModal(true);
  }, [intercomWorkspace, pendingInitialFlow]);

  if (isConnector && intercomWorkspace) return null;

  const isImportInitialFlow =
    importSettingModalState.modalState?.isInitialFlow ?? false;
  const isTriggerInitialFlow =
    triggerSettingModalState.modalState?.isInitialFlow ?? false;
  const isOnetimeInitialFlow =
    onetimeImportModalState.modalState?.isInitialFlow ?? false;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!intercomWorkspace}
        source={SOURCE_TYPE.INTERCOM}
        description="Import userchats as feedback into Syncly."
        connectedDisplay={intercomWorkspace?.name || 'Intercom workspace'}
        info={intercomWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={handleConnect}
        additionalActions={() => (
          <div className="flex gap-2">
            <Button
              variant="outlined"
              onClick={() => {
                void openTriggerSettingModal();
              }}
            >
              Trigger option
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                void openImportSettingModal();
              }}
            >
              Import option
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                void openOnetimeImportModal();
              }}
            >
              One-time import
            </Button>
          </div>
        )}
      />

      <Modal
        status={importSettingModalState.status}
        closeModal={closeModal}
        useBackgroundClose
      >
        <IntercomImportSettingModal
          importRule={importRule}
          template={intercomImportTemplate}
          loading={modalLoading}
          confirmOptions={{
            label: isImportInitialFlow ? 'Next' : 'Save change',
            callback: (rule) => saveImportOption(rule, isImportInitialFlow),
          }}
        />
      </Modal>

      <Modal
        status={triggerSettingModalState.status}
        closeModal={closeModal}
        useBackgroundClose
      >
        {intercomWorkspace && (
          <IntercomTriggerSettingModal
            importRule={importRule}
            template={intercomImportTemplate}
            loading={modalLoading}
            confirmOptions={{
              label: isTriggerInitialFlow ? 'Next' : 'Save change',
              callback: (rule) => saveTriggerOption(rule, isTriggerInitialFlow),
            }}
          />
        )}
      </Modal>

      <Modal
        status={onetimeImportModalState.status}
        closeModal={closeModal}
        useBackgroundClose
      >
        {intercomWorkspace && (
          <IntegrationImportModal
            isNew={isOnetimeInitialFlow}
            title={'Import Conversations'}
            subTitle={'Intercom'}
            subTitleList={intercomWorkspace.name}
            historyList={intercomWorkspace.history || []}
            importRule={importRule}
            template={intercomImportTemplate}
            onSaveWithoutImport={handleSaveWithoutImport}
            onSubmitImport={(start_date, end_date, importRuleValue) =>
              handleImportWorkspace(start_date, end_date, importRuleValue)
            }
          />
        )}
      </Modal>
    </>
  );
};
